import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

// material-ui
import { useTheme } from '@mui/material/styles';
import { Avatar, Badge, Box, ButtonBase, IconButton, Tooltip } from '@mui/material';

// project imports
import LogoSection from '../LogoSection';
import ProfileSection from './ProfileSection';
import ThemeButton from 'ui-component/ThemeButton';
import { adminTokens } from 'ui-component/admin/AdminPage';
import { drawerWidth, miniDrawerWidth } from 'store/constant';

// assets
import { IconMenu2 } from '@tabler/icons-react';
import { IconBell } from '@tabler/icons-react';
import { API } from 'utils/api';

// ==============================|| MAIN NAVBAR / HEADER ||============================== //

const Header = ({ handleLeftDrawerToggle }) => {
  const theme = useTheme();
  const account = useSelector((state) => state.account);
  const drawerOpen = useSelector((state) => state.customization.opened);
  const isHotel = account.user?.biz_role === 'hotel';
  const isHotelOwner = isHotel && account.user?.hotel_role === 'owner';
  const [unreadCount, setUnreadCount] = useState(0);
  const navigate = useNavigate();

  useEffect(() => {
    if (!isHotelOwner) { setUnreadCount(0); return undefined; }
    let active = true;
    const loadUnread = async () => {
      const response = await API.get('/api/hotel/notifications/unread-count');
      if (active && response.data.success) setUnreadCount(Number(response.data.data?.count || 0));
    };
    loadUnread();
    const timer = window.setInterval(loadUnread, 60000);
    return () => { active = false; window.clearInterval(timer); };
  }, [isHotelOwner]);

  const toggleButton = (
    <Tooltip title={drawerOpen ? '收起菜单' : '展开菜单'} placement="bottom" arrow>
      <ButtonBase aria-label={drawerOpen ? '收起菜单' : '展开菜单'} sx={{ borderRadius: '6px', overflow: 'hidden' }} onClick={handleLeftDrawerToggle}>
        <Avatar
          variant="rounded"
          sx={{
            ...theme.typography.commonAvatar,
            ...theme.typography.mediumAvatar,
            width: 36,
            height: 36,
            color: adminTokens.accent,
            background: adminTokens.hover,
            transition: theme.transitions.create(['background-color', 'color'], {
              easing: theme.transitions.easing.easeOut,
              duration: theme.transitions.duration.shorter
            }),
            '&:hover': {
              background: '#DDEDE8',
              color: adminTokens.accentHover
            }
          }}
          color="inherit"
        >
          <IconMenu2 stroke={1.5} size="1.3rem" />
        </Avatar>
      </ButtonBase>
    </Tooltip>
  );

  return (
    <Box sx={{ width: '100%', height: { xs: 64, md: 72 }, display: 'flex', alignItems: 'stretch' }}>
      <Box
        sx={{
          display: { xs: 'none', md: 'flex' },
          alignItems: 'center',
          justifyContent: drawerOpen ? 'space-between' : 'center',
          width: drawerOpen ? drawerWidth : miniDrawerWidth,
          flex: `0 0 ${drawerOpen ? drawerWidth : miniDrawerWidth}px`,
          px: drawerOpen ? 1.5 : 0,
          borderRight: `1px solid ${adminTokens.border}`,
          transition: theme.transitions.create(['width', 'flex-basis'], {
            easing: theme.transitions.easing.easeOut,
            duration: theme.transitions.duration.shorter
          })
        }}
      >
        {drawerOpen && (
          <Box
            sx={{
              minWidth: 0,
              overflow: 'hidden',
              '& .MuiButtonBase-root': { maxWidth: '100%' },
              '& img': { maxWidth: '100%', objectFit: 'contain' },
              '& .MuiTypography-h3': { fontSize: 20, lineHeight: 1.05 },
              '& .MuiTypography-caption': {
                display: 'block',
                maxWidth: 118,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap'
              }
            }}
          >
            <LogoSection />
          </Box>
        )}
        {toggleButton}
      </Box>

      <Box
        sx={{
          flex: 1,
          minWidth: 0,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          px: { xs: 2, md: 2.5 },
          gap: 1.5
        }}
      >
        <Box sx={{ display: { xs: 'flex', md: 'none' }, alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>{toggleButton}</Box>
        <Box sx={{ flexGrow: 1 }} />
        {!isHotel && (
          <Box sx={{ display: { xs: 'none', sm: 'block' } }}>
            <ThemeButton />
          </Box>
        )}
        {isHotelOwner && (
          <Tooltip title="经营通知">
            <IconButton aria-label="经营通知" onClick={() => navigate('/panel/hotel-notifications')} sx={{ color: adminTokens.accent }}>
              <Badge badgeContent={unreadCount} color="error" max={99}><IconBell size={21} /></Badge>
            </IconButton>
          </Tooltip>
        )}
        <ProfileSection />
      </Box>
    </Box>
  );
};

Header.propTypes = {
  handleLeftDrawerToggle: PropTypes.func
};

export default Header;
